/**
 * shared-firestore-subscription — collapse N identical client listeners into ONE.
 *
 * THE FIRESTORE-COST FIX (client side). Hooks like `useFirestoreCrypto` /
 * `useMarketAnalysis` / `useFirestoreCollections` used to open a *separate* Firestore
 * `onSnapshot` per component instance. Inline components (`TokenSymbol`, `TagMention`)
 * mount those hooks many times per page, so a single page view opened 5–30 listeners
 * over the SAME document(s) — each billed an initial read plus a re-read on every backend
 * rewrite (e.g. the 5-minute `crypto_prices` heartbeat), for the whole life of the tab.
 *
 * This primitive shares ONE underlying listener across all subscribers via reference
 * counting: the first subscriber opens it, late subscribers are seeded from the cached
 * value (no extra read), and it's torn down only when the last subscriber leaves. The
 * underlying `start` is dependency-injected (the caller wires Firestore), so this stays
 * pure + trivially testable and carries no Firebase import itself.
 */

/** Opens the real data source. Receives `emit`/`fail` callbacks; returns an unsubscribe. */
export type StartSubscription<T> = (
  emit: (value: T) => void,
  fail: (error: Error) => void,
) => () => void;

export interface SharedSubscription<T> {
  /**
   * Attach a subscriber. Returns an unsubscribe fn. If a value is already cached it's
   * delivered synchronously (no new listener / read); otherwise a cached error (if any)
   * is delivered to `onError`.
   */
  subscribe(onValue: (value: T) => void, onError?: (error: Error) => void): () => void;
}

export function createSharedSubscription<T>(start: StartSubscription<T>): SharedSubscription<T> {
  const valueListeners = new Set<(value: T) => void>();
  const errorListeners = new Set<(error: Error) => void>();

  let stop: (() => void) | null = null;
  let current: T | undefined;
  let hasValue = false;
  let lastError: Error | null = null;

  const ensureStarted = () => {
    if (stop) return;
    stop = start(
      (value) => {
        current = value;
        hasValue = true;
        lastError = null;
        valueListeners.forEach((l) => l(value));
      },
      (error) => {
        lastError = error;
        errorListeners.forEach((l) => l(error));
      },
    );
  };

  const teardown = () => {
    if (valueListeners.size > 0 || errorListeners.size > 0) return;
    stop?.();
    stop = null;
    // Drop cached state so a fresh subscription re-reads live data (avoids serving a
    // value captured before the listener was closed).
    current = undefined;
    hasValue = false;
    lastError = null;
  };

  return {
    subscribe(onValue, onError) {
      valueListeners.add(onValue);
      if (onError) errorListeners.add(onError);

      ensureStarted();

      // Seed the late subscriber from cache — no extra Firestore read.
      if (hasValue && current !== undefined) onValue(current);
      else if (lastError && onError) onError(lastError);

      let active = true;
      return () => {
        if (!active) return;
        active = false;
        valueListeners.delete(onValue);
        if (onError) errorListeners.delete(onError);
        teardown();
      };
    },
  };
}
